import { checkDateIsEqual, checkIsToday, getDatesInRange } from '../lib';
import { useDatePickerHook } from '../lib/hooks/useDatePickerHook';
import useClickOutside from '@shared/hooks/useClickOutside';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { DatePickerProps } from '../model/types';
import ChevronRight from './assets/ChevronRight';
import ChevronLeft from './assets/ChevronLeft';
import DownArrow from './assets/DownArrow';
import { Input } from '@shared/ui/kit/input';
import CloseIcon from './assets/CloseIcon';
import { CalendarIcon } from '@shared/assets/icons';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

const DatePicker: FC<DatePickerProps> = (props) => {
  const {
    locale = 'default',
    selectedDate,
    firstWeekDay = 2,
    isRange = true,
    width = '100%',
    size = 'small',
    labelText = 'Дата',
    placeholder = 'Выберите дату',
    inputStatus = 'default',
    inputMessage,
    onCalendarVisibilityChange,
    setNewSelectedDate,
  } = props;

  const { state, functions } = useDatePickerHook({
    firstWeekDay,
    locale,
    selectedDate,
    setNewSelectedDate,
    isRange,
  });

  const [hoveredDates, setHoveredDates] = useState<Date[]>([]);
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [, setDayInInput] = useState<string>('');
  const datePickerRef = useRef<HTMLDivElement>(null);
  const prevCalendarVisibleRef = useRef(false);
  const onCalendarVisibilityChangeRef = useRef(onCalendarVisibilityChange);
  onCalendarVisibilityChangeRef.current = onCalendarVisibilityChange;
  const hasSelectedValue = Boolean(state.prevDayInput);

  useEffect(() => {
    const wasOpen = prevCalendarVisibleRef.current;
    if (wasOpen !== isCalendarVisible) {
      onCalendarVisibilityChangeRef.current?.(isCalendarVisible);
    }
    prevCalendarVisibleRef.current = isCalendarVisible;
  }, [isCalendarVisible]);

  const onLeftClickHandler = () => {
    functions.onClickArrow('left');
  };
  const onRightClickHandler = () => {
    functions.onClickArrow('right');
  };

  useClickOutside(datePickerRef, () => setIsCalendarVisible(false));

  const mouseEnterHandler = (day: Date) => {
    if (isRange) {
      if (state.selectedRange[0]) {
        const startDay = new Date(state.selectedRange[0]);

        startDay.setDate(startDay.getDate() + 1);

        if (!state.selectedRange[1]) {
          setHoveredDates(getDatesInRange(startDay, day));
        } else {
          setHoveredDates([]);
        }
      }
    }
  };

  const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    setDayInInput(e.target.value);
  };
  return (
    <div style={{ position: 'relative' }}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          setIsCalendarVisible(true);
        }}
        {...stylex.props(styles.calendarInputWrapper(width))}
      >
        <Input
          labelText={labelText}
          placeholder={placeholder}
          size={size}
          type="text"
          value={state.prevDayInput}
          changeFunction={() => inputChangeHandler}
          settings={{ width }}
          status={inputStatus}
          inputMessage={inputMessage}
        />

        {hasSelectedValue ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              functions.setSelectedRange([null, null]);
              functions.setPrevDayInput('');
              props.setNewSelectedDate?.([null, null]);
              setHoveredDates([]);
            }}
            {...stylex.props(styles.closeIconWrapper)}
          >
            <CloseIcon size="small" />
          </span>
        ) : (
          <span {...stylex.props(styles.calendarIconWrapper)}>
            <CalendarIcon />
          </span>
        )}
      </div>
      {isCalendarVisible && (
        <div ref={datePickerRef} {...stylex.props(styles.calendar)}>
          <div {...stylex.props(styles.calendarContainer)}>
            <div {...stylex.props(styles.calendarHeader)} data-testid="calendar-header">
              <ChevronLeft onClick={onLeftClickHandler} size={size} />

              {state.mode === 'days' && (
                <>
                  <div {...stylex.props(styles.calendarHeaderTitle)} onClick={() => functions.setMode('monthes')}>
                    <span {...stylex.props(styles.calendarHeaderMonthes)}>
                      {state.monthesNames[state?.selectedMonth?.monthIndex].month} {state.selectedYear}
                    </span>
                    <DownArrow onClick={onRightClickHandler} size="small" />
                  </div>
                </>
              )}

              {state.mode === 'monthes' && <div onClick={() => functions.setMode('days')}>{state.selectedYear}</div>}

              <ChevronRight onClick={onRightClickHandler} size={size} />
            </div>
            <div {...stylex.props(styles.calendarBody)}>
              {state.mode === 'days' && (
                <>
                  <div {...stylex.props(styles.calendarWeekNames)}>
                    {state.weekDaysNames.map((weekDayName) => (
                      <div key={weekDayName.dayShort}> {weekDayName.dayShort} </div>
                    ))}
                  </div>
                  <div {...stylex.props(styles.calendarDays)}>
                    {state.calendarDays.map((day) => {
                      const isToday = checkIsToday(day.date);
                      const isInRange =
                        state.selectedRange?.length === 2 &&
                        state.selectedRange[0] &&
                        state.selectedRange[1] &&
                        day.date >= state.selectedRange[0] &&
                        day.date <= state.selectedRange[1];
                      const isAdditionalDay = day.monthIndex !== state.selectedMonth.monthIndex;
                      const isStartRange =
                        state.selectedRange &&
                        state.selectedRange[0] &&
                        checkDateIsEqual(day.date, state.selectedRange[0]);
                      const isEndRange =
                        state.selectedRange &&
                        state.selectedRange[1] &&
                        checkDateIsEqual(day.date, state.selectedRange[1]);

                      return (
                        <div
                          key={`${day.dayNumber}-${day.monthIndex}`}
                          {...stylex.props(
                            styles.calendarDay,
                            isToday ? styles.calendarTodayItem : null,
                            isRange && isInRange ? styles.calendarDaysInRange : null,
                            isAdditionalDay ? styles.calendarAdditionalDay : null,
                            isStartRange ? styles.calendarSelectedFirstDay(isRange) : null,
                            isRange && isEndRange ? styles.calendarSelectedEndDay : null,
                            hoveredDates.some((hoveredDate) => checkDateIsEqual(day.date, hoveredDate))
                              ? styles.calendarHoveredDay
                              : null
                          )}
                          onClick={() => {
                            functions.onDayClickHandler(day.date, isRange, setDayInInput, setIsCalendarVisible);
                            setHoveredDates([]);
                          }}
                          onMouseEnter={() => {
                            if (state.selectedRange[0] !== null) {
                              mouseEnterHandler(day.date);
                            }
                          }}
                        >
                          {day.dayNumber}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
              {state.mode === 'monthes' && (
                <div {...stylex.props(styles.calendarPickItemContainer)}>
                  {state.monthesNames.map((monthesName) => {
                    const isCurrentMonth =
                      new Date().getMonth() === monthesName.monthIndex &&
                      state.selectedYear === new Date().getFullYear();
                    const isSelectedMonth = monthesName.monthIndex === state.selectedMonth.monthIndex;
                    return (
                      <div
                        key={monthesName.month}
                        onClick={() => {
                          functions.setSelectedMonthByIndex(monthesName.monthIndex);
                          functions.setMode('days');
                        }}
                        {...stylex.props(
                          styles.calendarPickItem,
                          isSelectedMonth ? null : null,
                          isCurrentMonth ? styles.calendarTodayItem : null
                        )}
                      >
                        {monthesName.monthShort.slice(0, 3)}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
